import type { JSX, ReactNode } from 'react';

import { HelpCircle } from 'lucide-react';

import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ControlSettingCardProps {
  titleId: string;
  title: string;
  description: string;
  icon: ReactNode;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  switchDisabled?: boolean;
  switchSlot?: ReactNode;
  tooltip?: string;
  tooltipLabel?: string;
}

const ControlSettingCard = ({
  titleId,
  title,
  description,
  icon,
  checked,
  onCheckedChange,
  switchDisabled = false,
  switchSlot,
  tooltip,
  tooltipLabel,
}: ControlSettingCardProps): JSX.Element => (
  <div
    className={cn('flex items-start gap-4 rounded-xl border border-border-subtle bg-bg-surface px-5 py-4 shadow-xs')}
  >
    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand-navy">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex min-w-0 items-start gap-1">
        <h4 id={titleId} className="m-0 min-w-0 text-sm font-semibold leading-tight text-brand-navy font-display">
          {title}
        </h4>
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={tooltipLabel ?? `Más información sobre ${title}`}
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-text-muted transition-colors
                  hover:bg-bg-surface-raised hover:text-brand-navy focus-visible:outline-none focus-visible:ring-3
                  focus-visible:ring-ring/50"
              >
                <HelpCircle className="size-3.5" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm text-left leading-relaxed">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <p className="m-0 mt-1 text-sm leading-relaxed text-text-muted font-body">{description}</p>
    </div>
    <div className="flex shrink-0 items-start pt-0.5">
      {switchSlot ?? (
        <Switch
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={switchDisabled}
          aria-labelledby={titleId}
          className="data-checked:bg-brand-vivid"
        />
      )}
    </div>
  </div>
);

export default ControlSettingCard;
